import { useId, useState } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { DayPicker } from '@daypicker/react'
import { CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPickedDate, parseCalendarDate, toCalendarDate } from '@/lib/calendar-date'

/**
 * A calendar date picker that names the weekday (CAP-862).
 *
 * A native `<input type="date">` renders whatever the browser's locale says -
 * `10/12/2026` - and cannot be told to show the day of the week, while a crew
 * asks to move a cleaning "to Monday". So the trigger reads "Mon 12 Oct", and
 * the month grid opens in the admin's own theme instead of the browser's.
 *
 * The value is a calendar date, `YYYY-MM-DD`, exactly what a date input would
 * hand back, so swapping one for the other changes no caller's arithmetic. It
 * is converted to a local-midnight `Date` only for the grid, which counts days
 * in the browser's zone; a date has no time, so there is nothing to shift.
 */

interface DatePickerProps {
  id?: string
  /** `YYYY-MM-DD`, or empty for no date yet. */
  value: string
  onChange: (value: string) => void
  /**
   * `YYYY-MM-DD` in the zone the dates belong to: marks today in the grid and
   * decides when the year needs spelling out. Falls back to the browser's day.
   */
  today?: string | null
  disabled?: boolean
  className?: string
  /**
   * The id of the visible label. The trigger is then named by the label AND
   * the date it shows ("Date, Mon 12 Oct"): a `<label for>` alone would name
   * the button "Date" and hide the value from a screen reader.
   */
  labelledBy?: string
}

export function DatePicker({
  id,
  value,
  onChange,
  today,
  disabled,
  className,
  labelledBy,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const valueId = useId()
  const selected = parseCalendarDate(value)
  const todayDate = parseCalendarDate(today)
  const label = formatPickedDate(value, today)

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        id={id}
        type="button"
        disabled={disabled}
        aria-labelledby={labelledBy ? `${labelledBy} ${valueId}` : undefined}
        className={cn(
          'inline-flex h-8 items-center gap-2 rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50 data-[state=open]:border-mint',
          className
        )}
      >
        <CalendarDays className="h-4 w-4 shrink-0 text-textMuted" aria-hidden="true" />
        <span id={valueId} className={cn('whitespace-nowrap', !label && 'text-textFaint')}>
          {label ?? 'Pick a date'}
        </span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={16}
          // Radix would focus the first button - "previous month". The day
          // to start from is the grid's own tab stop (the picked day, else
          // today), where the arrow keys move from.
          onOpenAutoFocus={event => {
            const day = (event.currentTarget as HTMLElement).querySelector<HTMLElement>(
              '[role="grid"] button[tabindex="0"]'
            )
            if (!day) return
            event.preventDefault()
            day.focus()
          }}
          className="z-50 rounded-md border border-borderStrong bg-surface p-3 text-text shadow-lg"
        >
          <DayPicker
            mode="single"
            required
            selected={selected}
            defaultMonth={selected ?? todayDate}
            today={todayDate}
            onSelect={date => {
              onChange(toCalendarDate(date))
              setOpen(false)
            }}
            classNames={{
              root: 'relative',
              months: 'flex',
              month: 'space-y-2',
              month_caption: 'flex h-8 items-center justify-center',
              caption_label: 'text-sm font-semibold',
              nav: 'absolute inset-x-0 top-0 flex h-8 items-center justify-between',
              button_previous:
                'inline-flex h-8 w-8 items-center justify-center rounded-input text-textMuted hover:bg-surface2 hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-30',
              button_next:
                'inline-flex h-8 w-8 items-center justify-center rounded-input text-textMuted hover:bg-surface2 hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-30',
              chevron: 'h-4 w-4 fill-current',
              month_grid: 'border-collapse',
              weekdays: 'flex',
              weekday: 'w-9 text-center text-xs font-normal text-textFaint',
              week: 'mt-1 flex',
              day: 'h-9 w-9 p-0 text-center text-sm',
              day_button:
                'inline-flex h-9 w-9 items-center justify-center rounded-input text-text hover:bg-surface2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint',
              today: '[&>button]:font-semibold [&>button]:text-mint',
              selected:
                '[&>button]:bg-mint [&>button]:font-semibold [&>button]:text-charcoal [&>button]:hover:bg-mint',
              outside: '[&>button]:text-textFaint',
              disabled: '[&>button]:opacity-30',
            }}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
